import { useContext, useEffect } from 'react'
import { classes } from 'typestyle'
import { useLocation, useParams } from 'react-router-dom'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Core ========================================================================================= */
import { GLOBAL_CONTENTS } from '../../../common/global-contents'
import { addSnippet } from '../../../core/snippet-manager'
import { flat, structureObj } from '../../../share/obj-equal'
import { get } from '../../../share/find'
import { withRouter } from '../../../contexts/with-router'

/* Hooks ======================================================================================== */
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { EvenMoreSimplerPagination } from '../../../modules/navigation/pagination'
import { FIELDS } from '../../../share/fields'
import { Form } from '../../../components/form'
import { Line } from '../../../components/line'
import { Modal } from '../../../components/modal'
import { Notification } from '../../../components/notification'
import { Snippet, SnippetHead, SnippetBody } from '../../../modules/layouts/snippet'
import { Text } from '../../../components/text/text.component'
import { TextField } from '../../../components/text-field'
import { getCurrentUser } from '../../../services/auth'
import { getSubtitle, getTitle } from '../../routes/row'
import { renderForm } from '../../../modules/handlers/render-form'

/* Styles ======================================================================================= */
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { ml4 } from '../../../styles/styleset/margin/ml4'
import { mr4 } from '../../../styles/styleset/margin/mr4'
import { mt16 } from '../../../styles/styleset/margin/mt16'
import { mt8 } from '../../../styles/styleset/margin/mt8'
import { p12 } from '../../../styles/styleset/padding/p12'
import { ml8 } from '../../../styles/styleset/margin/ml8'

export default addSnippet(
  'Replies',
  withRouter((props: any) => {
    const location = useLocation()
    const { title, sync, row } = props
    const { groupId } = useParams()
    const { navigation } = useContext(NavigationContext)

    const [value, $value] = useValue('')
    const [isPosting, $isPosting] = useValue(false)
    const { currentUser } = getCurrentUser()
    const [local] = useLocal()

    const url = props.url
      ?.replace?.('{{parentId}}', row?.parentRow?.id)
      ?.replace?.('{{id}}', row?.id)

    const apiUrl = `${process.env.REACT_APP_API_URL}/a/groups/${groupId}/base/replies/table`
    const [data, $data] = useFetch(
      { data: [] },
      {
        url: apiUrl,
        search: {
          limit: 10,
          page: 1,
          'body.url': { equal: (Object.keys(row || {}).length && url) || location.pathname },
          sort: { key: 'createdAt', direction: -1 },
        },
      },
    )

    const apiPostUrl = `${process.env.REACT_APP_API_URL}/a/groups/${groupId}/base/replies/table/new`
    const [, $post] = useFetch({}, { url: apiPostUrl })

    const apiDeletePostURL = `${process.env.REACT_APP_API_URL}/a/delete-reply/:groupId/:rowId`
    const [_delete, $delete] = useFetch({}, { url: apiDeletePostURL })

    useEffect(() => {
      if (Object.keys(row || {}).length && data.mounted) {
        $data.get({
          url: apiUrl,
          search: {
            limit: 10,
            page: 1,
            'body.url': { equal: (Object.keys(row || {}).length && url) || location.pathname },
            sort: { key: 'createdAt', direction: -1 },
          },
        })
      }
    }, [data.mounted, row])

    const handleSubmit = () => {
      $value.set('')
      $isPosting.set(true)

      $post
        .post({
          body: {
            body: {
              message: value,
              url: (Object.keys(row || {}).length && url) || location.pathname,
              writtenById: currentUser?.id,
              writtenBy: currentUser?.fullName,
              base: navigation.payload.base.id,
              type: navigation.payload.base.type,
              title: handleTitle(navigation.payload.base, row),
            },
          },
        })
        .then(() => {
          $data
            .get({
              url: apiUrl,
              search: {
                limit: 10,
                page: 1,
                'body.url': { equal: (Object.keys(row || {}).length && url) || location.pathname },
                sort: { key: 'createdAt', direction: -1 },
              },
            })
            .then(() => {
              $isPosting.set(false)
              sync()
            })
        })
    }

    return (
      <Snippet>
        <SnippetHead title={title}>
          <EvenMoreSimplerPagination data={data} $data={$data} size="xs" />
        </SnippetHead>
        <SnippetBody>
          <Box baseline="none">
            <Box rounded baseline="background">
              <TextField
                size="md"
                baseline="background"
                placeholder={GLOBAL_CONTENTS.placeholderMessage[local]}
                alignRight
                disableAsyncValidation
                value={value}
                onChange={(data: any) => $value.set(data)}
                onKeyDown={e => {
                  if (e.keyCode === 13 && value) {
                    handleSubmit()
                  }
                }}
              >
                <Button
                  label={GLOBAL_CONTENTS.send[local]}
                  className={mr4}
                  size="sm"
                  baseline={isPosting || !value ? 'disabled' : 'primary'}
                  onClick={handleSubmit}
                  square
                  alt
                />
              </TextField>
            </Box>
          </Box>
          <Box row baseline="none">
            {data.payload.data.map((item: any, key) => {
              const { id, createdAt, updatedBy } = item
              const { writtenBy, writtenById, message, deleted } = item.body
              return (
                <Box
                  baseline="background"
                  className={classes(mt16, p12)}
                  key={key}
                  rounded
                  row
                  border
                >
                  <Box baseline="none" className={mb8}>
                    <Text size="sm" baseline="surface" shrink uppercase semiBold lighter>
                      {new Date(createdAt).toLocaleString(process.env.REACT_APP_LANGUAGE)}
                    </Text>
                  </Box>
                  <Text size="sm" className={mb8} semiBold>
                    {writtenBy}
                  </Text>
                  <Text size="sm" className={mb4}>
                    {deleted ? (
                      <Text size="sm" baseline="surface" lighter>
                        ({GLOBAL_CONTENTS.deleted[local]})
                      </Text>
                    ) : (
                      <>
                        <Text size="sm">{message} </Text>
                        {updatedBy && (
                          <Text size="sm" baseline="surface" lighter>
                            ({GLOBAL_CONTENTS.edited[local]})
                          </Text>
                        )}
                      </>
                    )}
                  </Text>
                  {writtenById === currentUser.id && !deleted && (
                    <Box baseline="none" className={mt8} flex alignRight>
                      <Button
                        baseline="background"
                        size="sm"
                        label={
                          _delete.meta.isLoading
                            ? GLOBAL_CONTENTS.deleting[local]
                            : GLOBAL_CONTENTS.delete[local]
                        }
                        square
                        onClick={() => {
                          $delete
                            .post({
                              url: `${process.env.REACT_APP_API_URL}/a/delete-reply/${groupId}/${item.id}`,
                            })
                            .then(() => {
                              $data.get({
                                url: apiUrl,
                                search: {
                                  limit: 10,
                                  page: 1,
                                  'body.url': {
                                    equal:
                                      (Object.keys(row || {}).length && url) || location.pathname,
                                  },
                                  sort: { key: 'createdAt', direction: -1 },
                                },
                              })
                            })
                        }}
                      />
                      <Button
                        className={ml4}
                        baseline="background"
                        size="sm"
                        label={GLOBAL_CONTENTS.edit[local]}
                        square
                        onClick={() => {
                          Modal.push({
                            type: 'ReplyEdit',
                            componentProps: {
                              value: message,
                              pathName: `/a/groups/${groupId}/base/replies/table/${id}`,
                              onUpdate: () => {
                                $data.get({
                                  url: apiUrl,
                                  search: {
                                    limit: 10,
                                    page: 1,
                                    'body.url': {
                                      equal:
                                        (Object.keys(row || {}).length && url) || location.pathname,
                                    },
                                    sort: { key: 'createdAt', direction: -1 },
                                  },
                                })
                              },
                            },
                          })
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </SnippetBody>
      </Snippet>
    )
  }),
)

const handleTitle = (base: any, target: any) => {
  const { type } = base
  const name = get('name', base, 'object')
  switch (type) {
    case 'row':
      return `${getSubtitle({
        base: base,
        data: target,
        name,
      })} | ${getTitle({
        base: base,
        data: target,
        name,
      })}`
    case 'table':
      return get('plural', name, 'string')
    default:
      return get('singular', name, 'string')
  }
}

Modal.add('ReplyEdit', (props: any) => {
  const { field, pathName, value, onUpdate, parentName, fields, onSubmit } = props
  const [local] = useLocal()
  const [patch, $patch] = useFetch()
  const [values, $values] = useValue({
    ['body.message']: value == '-' ? '' : value,
  })
  return (
    <Box onClick={props.handleContentClick} rounded minWidth={360}>
      <Box className={classes(p12)} onlyContrast fullWidth>
        <Text size="lg" semiBold>
          {GLOBAL_CONTENTS.editComment[local]}
        </Text>
      </Box>
      <Line />
      <Form
        values={structureObj(values)}
        onSubmit={(body: any) => {
          $values.set(flat(body))

          if (onSubmit) {
            onSubmit(body)
            Modal.pop()
          } else {
            $patch
              .patch({
                url: `${process.env.REACT_APP_API_URL}${pathName}`,
                body,
              })
              .then(() => {
                Modal.pop()
                if (onUpdate) {
                  onUpdate()
                  Notification.push({ label: GLOBAL_CONTENTS.updatedSuccessfully[local] })
                }
              })
          }
        }}
      >
        <Box className={p12}>
          {renderForm({
            field: {
              name: 'body.message',
              type: FIELDS.TEXT,
              label: GLOBAL_CONTENTS.body[local],
            },
            parentName: '',
            values,
            entryUrl: `${process.env.REACT_APP_API}/app`,
          })}
        </Box>
        <Line />
        <Box className={p12} baseline="none" spaceBetween fullWidth>
          <Box />
          <Box baseline="none" spaceBetween>
            <Box />
            <Box column>
              <Button
                onClick={Modal.pop}
                label={GLOBAL_CONTENTS.close[local]}
                baseline="background"
                size="sm"
                square
              />
              <Button
                className={ml8}
                label={GLOBAL_CONTENTS.save[local]}
                baseline="primary"
                size="sm"
                square
                type="submit"
              />
            </Box>
          </Box>
        </Box>
      </Form>
    </Box>
  )
})
